import Link from 'next/link';
import type { PropsWithChildren } from 'react';
import { memo } from 'react';

import { useT } from '@/app/i18n/client';
import { useCurrency } from '@/features/product/contexts/CurrencyContext';
import { cn, utilFromMinorUnit } from '@/lib/utils';

interface IProps {
  title: string;
  description: string;
  descriptionGoto?: string;
  price?: number;
  shouldShow: boolean;
  isActive?: boolean;
  isDisablePrice?: boolean;
  onToggleExpanded: () => void;
}

function ProductListItem({
  title,
  description,
  descriptionGoto,
  price = 0,
  shouldShow,
  isActive,
  isDisablePrice,
  onToggleExpanded,
  children,
}: PropsWithChildren<IProps>) {
  const { t } = useT('product');

  const currency = useCurrency();

  return (
    <article
      className={cn(
        'rounded-3xl border border-[#E9ECEF] bg-white transition-all duration-300 hover:border-[#0064FF]',
        !isActive && 'cursor-not-allowed opacity-50',
        shouldShow && 'border-[#0064FF]',
      )}
    >
      <div
        className={cn(
          'flex flex-col items-start justify-between gap-4 p-4 sm:p-6 md:flex-row md:items-center',
          isActive && 'cursor-pointer',
        )}
        onClick={isActive ? onToggleExpanded : undefined}
      >
        <div className='w-full space-y-1'>
          <div className='flex items-center justify-between gap-3'>
            <h2 className='text-2xl font-bold tracking-tight text-[#2E2E2E] max-sm:text-lg'>
              {title}
            </h2>
          </div>

          <p className='break-words text-base text-gray-600 max-sm:text-sm'>{description}</p>

          {descriptionGoto && (
            <div
              className={cn(
                'overflow-hidden transition-all duration-500 ease-in-out',
                shouldShow ? 'mt-1 max-h-10 opacity-100' : 'mt-0 max-h-0 opacity-0',
              )}
            >
              <Link href={`#${descriptionGoto}`} onClick={e => e.stopPropagation()}>
                <span
                  className={cn(
                    'text-sm text-[#2E2E2E] underline transition-transform duration-500 ease-in-out',
                    shouldShow ? 'translate-y-0' : 'translate-y-2',
                  )}
                >
                  {t('readMore')}
                </span>
              </Link>
            </div>
          )}

          <div
            className={cn(
              'ml-auto gap-8 text-right transition-opacity',
              shouldShow ? 'opacity-0' : 'opacity-100',
            )}
          >
            <div className='flex flex-col items-end gap-2 text-right'>
              <div className='flex items-center gap-2'>
                {isDisablePrice ? (
                  <p className='text-sm text-[#DCDCDC]'>{t('showMore')}</p>
                ) : (
                  <p className='text-2xl font-bold text-[#2E2E2E] max-sm:text-lg'>
                    {currency} {utilFromMinorUnit(price, currency)}~
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'overflow-hidden transition-all duration-500 ease-in-out',
          shouldShow ? 'max-h-[9999px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div
          className={cn(
            'transform transition-all duration-500 ease-in-out',
            shouldShow ? 'translate-y-0 scale-100' : '-translate-y-4 scale-95',
          )}
        >
          <div
            className={cn(
              'px-4 transition-all delay-100 duration-700 sm:px-6',
              shouldShow ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
              shouldShow && 'pb-8',
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </article>
  );
}

export default memo(ProductListItem);
